import React, { useState, useEffect, useRef } from 'react';
import {
  Menu, X, ChevronLeft, ChevronRight, CheckCircle,
  Users, Target, Mail, Moon, Sun,
  MapPin,
  Briefcase, Brain, Cpu,
  ShieldAlert,
  DollarSign,
  CalendarDays,
  LayoutDashboard,
  Layers,
  Users2,
  Repeat,
  BookOpen,
  Map,
  Truck,
  Lightbulb,
  ClipboardCheck,
  Package,
  Sparkles
} from 'lucide-react';
import { Facebook, Instagram, YouTube, LinkedIn } from '@mui/icons-material';
import './App.css';
import Background1 from '../src/assets/background1.jpg';  
import Background2 from '../src/assets/background2.jpg';
import Background3 from '../src/assets/background3.jpg';
import logo from '/logo-web.png';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

// Fix Leaflet marker icons (required for Vite/React setups)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const PragPaceWebsite = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Home");
  const [isDarkMode, setIsDarkMode] = useState(false);

  const homeRef = useRef(null);
  const solutionsRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);
  const learnMoreRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToUserFocus = () => {
    learnMoreRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToConatctFocus = () => {
    contactRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToContact = () => {
    window.open('https://www.youtube.com/@pragpace', '_blank');
  };

  const slides = [
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Connected Framework",
      description: "Integrates all project elements — Cost, Schedule, Risk, Scope, Quality, and Resources — into one unified ecosystem",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Customized Health Board",
      description: "Visual, department-wise overview of project status, risks, and KPIs with custom metrics for instant health checks and quick decisions.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "AI Enabled",
      description: "Voice-based updates and entries for on-the-go status capture — enabling smart multitasking for managers and teams.",
      image: Background3
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Requirement Management",
      description: "Captures and links all requirements with schedule, cost, and risk; ensures business-to-design alignment.",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Risk Management",
      description: "Integrated with schedule and requirements; auto impact on cost and timeline with risk heat maps and priority scoring.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Cost Management",
      description: "Tracks budgets, expenses, and forecasts by department, resource, and material. Auto cost roll-up from schedule — no manual effort.",
      image: Background3
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Schedule Management",
      description: "Plans and tracks activities, milestones, and dependencies. Auto EVMS (SPI, CPI, EAC) and quality metrics (OTD, NC, FPY).",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Focus/Scrum Board",
      description: "Auto-generated from schedule — highlights key priorities, critical issues, and departmental action items.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Dashboard",
      description: "Smart, ready-to-use dashboards showing KPIs, performance, and productivity across teams, projects, and portfolios.",
      image: Background3
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Quality Management",
      description: "Integrated with schedule — monitors OTD, NCs, FPY, and EVMS metrics in real time.",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Scope Management",
      description: "Defines deliverables and approvals to prevent scope creep. Fully linked with change and schedule modules.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Resource Management",
      description: "Optimizes people, tools, and materials by department and project. Tracks utilization, performance, and availability.",
      image: Background3
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Change Requests",
      description: "Manages all changes with cost, scope, and schedule impact. Ensures traceability with approval workflow.",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Lessons Learnt",
      description: "Captures and classifies learnings by project and department to drive continuous improvement.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Roadmap",
      description: "Auto-generated from projects — provides strategic, portfolio-level timelines connecting org goals, product plans, and technology roadmaps.",
      image: Background3
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Delivery Tracker",
      description: "Auto-generated from the schedule module to track customer deliveries effectively; linked with project OTD and FPY metrics.",
      image: Background1
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Procurement Management",
      description: "Provides end-to-end tracking of all components, seamlessly linked to the corresponding procurement line items in the schedule.",
      image: Background2
    },
    {
      title: "PragPace AllSyncc Project and Portfolio Management",
      subtitle: "Innovation",
      description: "Establishes the baseline research mindset needed to identify, document, and track project-related patents and trade secrets.",
      image: Background3
    },
  ];

  useEffect(() => {
    const slideInterval = 10000;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, slideInterval);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  const isDesktop = window.innerWidth > 768;

  const userFocusData = [
    {
      icon: <Target size={48} color="#7c3aed" />,
      title: 'Connected Framework',
      tooltip: 'Integrates all project elements — Cost, Schedule, Risk, Scope, Quality, and Resources — into one unified ecosystem.'
    },
    {
      icon: <Users size={48} color="#10b981" />,
      title: 'Customized Health Board',
      tooltip: 'Visual, department-wise overview of project status, risks, and KPIs with custom metrics for instant health checks and quick decisions.'
    },
    {
      icon: <Brain size={48} color="#3b82f6" />,
      title: 'AI Enabled',
      tooltip: 'Voice-based updates and entries for on-the-go status capture — enabling smart multitasking for managers and teams.'
    }
  ];

  const modulesData = [
    {
      title: "Requirement Management",
      color: "#7c3aed",
      icon: <ClipboardCheck size={32} />,
      tooltip: "Captures and links all requirements with schedule, cost, and risk; ensures business-to-design alignment."
    },
    {
      title: "Risk Management",
      color: "#ef4444",
      icon: <ShieldAlert size={32} />,
      tooltip: "Integrated with schedule and requirements; auto impact on cost and timeline with risk heat maps and priority scoring."
    },
    {
      title: "Cost Management",
      color: "#10b981",
      icon: <DollarSign size={32} />,
      tooltip: "Tracks budgets, expenses, and forecasts by department, resource, and material. Auto cost roll-up from schedule — no manual effort."
    },
    {
      title: "Schedule Management",
      color: "#3b82f6",
      icon: <CalendarDays size={32} />,
      tooltip: "Plans and tracks activities, milestones, and dependencies. Auto EVMS (SPI, CPI, EAC) and quality metrics (OTD, NC, FPY)."
    },
    {
      title: "Focus/Scrum Board",
      color: "#f59e0b",
      icon: <Target size={32} />,
      tooltip: "Auto-generated from schedule — highlights key priorities, critical issues, and departmental action items."
    },
    {
      title: "Dashboard",
      color: "#8b5cf6",
      icon: <LayoutDashboard size={32} />,
      tooltip: "Smart, ready-to-use dashboards showing KPIs, performance, and productivity across teams, projects, and portfolios."
    },
    {
      title: "Quality Management",
      color: "#14b8a6",
      icon: <CheckCircle size={32} />,
      tooltip: "Integrated with schedule — monitors OTD, NCs, FPY, and EVMS metrics in real time."
    },
    {
      title: "Scope Management",
      color: "#f43f5e",
      icon: <Layers size={32} />,
      tooltip: "Defines deliverables and approvals to prevent scope creep. Fully linked with change and schedule modules."
    },
    {
      title: "Resource Management",
      color: "#6366f1",
      icon: <Users size={32} />,
      tooltip: "Optimizes people, tools, and materials by department and project. Tracks utilization, performance, and availability."
    },
    {
      title: "Change Requests",
      color: "#22c55e",
      icon: <Repeat size={32} />,
      tooltip: "Manages all changes with cost, scope, and schedule impact. Ensures traceability with approval workflow."
    },
    {
      title: "Lessons Learnt",
      color: "#eab308",
      icon: <BookOpen size={32} />,
      tooltip: "Captures and classifies learnings by project and department to drive continuous improvement."
    },
    {
      title: "Roadmap",
      color: "#3b82f6",
      icon: <Map size={32} />,
      tooltip: "Auto-generated from projects — provides strategic, portfolio-level timelines connecting org goals, product plans, and technology roadmaps."
    },
    {
      title: "Delivery Tracker",
      color: "#ec4899",
      icon: <Truck size={32} />,
      tooltip: "Auto-generated from the schedule module to track customer deliveries effectively; linked with project OTD and FPY metrics."
    },
    {
      title: "Procurement Management",
      color: "#06b6d4",
      icon: <Package size={32} />,
      tooltip: "Provides end-to-end tracking of all components, seamlessly linked to the corresponding procurement line items in the schedule."
    },
    {
      title: "Innovation",
      color: "#f97316",
      icon: <Sparkles size={32} />,
      tooltip: "Establishes the baseline research mindset needed to identify, document, and track project-related patents and trade secrets."
    },
  ];

  const solutions = [
    {
      title: "Corporate Solutions",
      desc: "PragPace AllSync Project and Portfolio Management",
      details: "Our Corporate Solutions empower organizations to streamline workflows, manage large-scale projects efficiently, and ensure seamless communication across departments.",
      color: "#7c3aed",
      icon: <Briefcase size={40} />,
      buttonFlag: true,
    },
    {
      title: "AI Solutions",
      desc: "Coming Soon...",
      details: "This feature under implementation coming soon",
      color: "#10b981",
      icon: <Brain size={40} />,
      buttonFlag: false,
    },
    {
      title: "Operating Solutions",
      desc: "Coming Soon...",
      details: "This feature under implementation coming soon",
      color: "#3b82f6",
      icon: <Cpu size={40} />,
      buttonFlag: false,
    },
  ];

  const NavBar = () => (
    <nav className={`navbar ${isDarkMode ? 'dark' : ''}`}>
      <div className="nav-container">
        {/* 🔄 Replaced SVG with image but kept same layout & proportions */}
        <div className="nav-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '250px',
              height: '58px',
              borderRadius: '50%',
              padding: '0px',
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '50%',
                width: '100%',    
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={logo}
                alt="PragPace Logo"
                style={{
                  height: '100%',
                  width: '100%',
                }}
              />
            </div>
          </div>
        </div>

        {/* ✅ Menu items */}
        <div className="nav-menu">
          {['Home', 'Solutions', 'About Us', 'Contact'].map((item) => (
            <button
              key={item}
              onClick={() => {
                scrollToSection(
                  item === 'Home'
                    ? homeRef
                    : item === 'Solutions'
                      ? solutionsRef
                      : item === 'About Us'
                        ? aboutRef
                        : contactRef
                );
                setActiveItem(item);
              }}
              className={`nav-item ${activeItem === item ? 'active' : ''}`}
            >
              {item}
              {activeItem === item && <span className="nav-underline"></span>}
            </button>
          ))}
          <button onClick={() => setIsDarkMode(!isDarkMode)} className="theme-toggle">
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>

        {/* ✅ Hamburger menu (unchanged) */}
        <button className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* ✅ Mobile Menu (unchanged) */}
      {isMenuOpen && (
        <div className="mobile-menu">
          {['Home', 'Solutions', 'About Us', 'Contact'].map((item) => (
            <button
              key={item}
              onClick={() => {
                setIsMenuOpen(false);
                scrollToSection(
                  item === 'Home'
                    ? homeRef
                    : item === 'Solutions'
                      ? solutionsRef
                      : item === 'About Us'
                        ? aboutRef
                        : contactRef
                );
              }}
              className={`mobile-item ${activeItem === item ? 'active' : ''}`}
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </nav>
  );



  const Card = ({ children, className = "", hover = true }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <div
        className={`card ${className} ${isHovered && hover ? 'hovered' : ''} ${isDarkMode ? 'dark' : ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
    );
  };

  const Button = ({ children, variant = 'contained', onClick, disabled = false }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`btn btn-${variant} ${disabled ? 'disabled' : ''}`}
    >
      {children}
    </button>
  );

  const Tooltip = ({ text, children }) => {
    const [show, setShow] = useState(false);
    return (
      <div className="tooltip-container" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
        {children}
        {show && <div className="tooltip">{text}</div>}
      </div>
    );
  };

  const HomePage = () => (
    <div className={`home-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="slider-container">
        {slides.map((slide, index) => (
          <div key={index} className={`slide ${index === currentSlide ? 'active' : ''}`}>
            <div className="slide-image" style={{ backgroundImage: `url(${slide.image})` }} />
            <div className="slide-overlay" />
            <div className="slide-content">
              <h1 className="slide-title">{slide.title}</h1>
              <h2 className="slide-subtitle">{slide.subtitle}</h2>
              <p className="slide-description">{slide.description}</p>
              <div className="slide-buttons">
                <Button onClick={scrollToUserFocus}>Learn More</Button>
                <Button onClick={scrollToContact}>Watch Demo</Button>
              </div>
            </div>
          </div>
        ))}

        {isDesktop && (
          <>
            <button className="slider-nav prev" onClick={prevSlide}>
              <ChevronLeft size={32} />
            </button>
            <button className="slider-nav next" onClick={nextSlide}>
              <ChevronRight size={32} />
            </button>
          </>
        )}

        <div className="slider-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
            />
          ))}
        </div>
      </div>

      <div className="user-focus-section">
        {userFocusData.map((item) => (
          <Card className="focus-card">
            <div className="focus-icon">{item.icon}</div>
            <div className="focus-title">{item.title}</div>
            <div className="solution-desc">{item.tooltip}</div>
          </Card>
        ))}
      </div>
    </div>
  );

  const SolutionsPage = () => (
    <div className={`section ${isDarkMode ? 'dark' : ''}`}>
      <div className="container">
        <h1 className="section-title gradient-text">Our Solutions</h1>
        <p className="section-subtitle">Comprehensive PragPace tools tailored to your needs</p>
        <div className="solutions-grid">
          {solutions.map((solution, idx) => (
            <Card key={idx}>
              <div className="solution-header" style={{ backgroundColor: solution.color }} />
              <div className="solution-content">
                <div style={{ color: solution.color, marginBottom: '16px' }}>{solution.icon}</div>
                <h3 className="solution-title">{solution.title}</h3>
                <p className="solution-desc">{solution.desc}</p>
                <Button
                  variant="contained"
                  disabled={!solution.buttonFlag}
                  onClick={solution.buttonFlag ? scrollToUserFocus : undefined}
                >
                  Learn More
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  ); 

  const LearnMorePage = () => (
    <div className={`section learn-more-section ${isDarkMode ? 'dark' : ''}`}>
      <div className="container">
       <h6 className="int-title gradient-text" style={{fontSize:"3.0rem",textAlign:"center"}}>Corporate Solutions</h6>
        <h6 className="int-title gradient-text" style={{fontSize:"1.9rem",textAlign:"center"}}>PragPace AllSyncc Project & Portfolio Management</h6>
        <div className="learn-more-content">
          <div className="action-buttons">
            <a href="https://www.youtube.com/@pragpace" target="_blank" rel="noopener noreferrer" className="btn btn-contained">
              Watch Demo
            </a>
            {['Deployment', 'Pricing', 'Discount Coupons'].map((text, idx) => (
              <button key={idx} onClick={scrollToConatctFocus} className="btn btn-contained">
                {text}
              </button>
            ))}
          </div>

          <div className="modules-grid">
            {modulesData.map((mod, idx) => (
              <Tooltip key={idx} text={mod.tooltip}>
                <div
                  className="module-card"
                  style={{
                    background: `linear-gradient(145deg, ${mod.color}1A, ${mod.color}33)`,
                    borderLeft: `4px solid ${mod.color}`, borderRight: `4px solid ${mod.color}`
                  }}
                >
                  <span className="module-title">{mod.title}</span>
                  <div className="module-icon" style={{ color: mod.color }}>
                    {mod.icon}
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className={`section ${isDarkMode ? 'dark' : ''}`}>
      <div className="container">
        <h1 className="section-title gradient-text">About PragPace</h1>
        <Card className="about-card">
          <p className="about-text">
            PragPace Solutions Pvt Ltd is a software and AI solutions company dedicated to delivering innovative products and services that address the evolving digital needs of the modern world.
          </p>
          <p className="about-text">
            The name "Prag" originates from Pragnyana — the ultimate form of knowledge as revealed in the Vedic texts. By combining this depth of wisdom with "Pace", symbolizing agility and progress, PragPace embodies the perfect harmony of insight and speed to deliver transformative solutions for an ever-evolving market.
          </p>
          <p className="about-text">
            Founded by experienced industry and technology professionals, PragPace strives to harness knowledge and innovation to solve the complex challenges of the digital and AI-driven era.
          </p>
          <p className="about-text">
            In the long term, PragPace envisions developing a fully indigenous operating system — built in India, for the world — empowering digital sovereignty and advancing global technological independence.
          </p>

          <div className="values-grid">
            <div className="value-card vision">
              <h2>Vision</h2>
              <p>PragPace envisions developing a fully indigenous operating system — built in India, for the world — empowering digital sovereignty and advancing global technological independence.</p>
            </div>
            <div className="value-card mission">
              <h2>Mission</h2>
              <p>Our mission is to empower teams with ultimate knowledge and accelerated workflows, enabling them to focus on what matters most — delivering exceptional results.</p>
            </div>
            <div className="value-card values">
              <h2>Values</h2>
              <p>PragPace values innovation, knowledge, and agility. We believe in fostering a culture of continuous learning, collaboration, and ethical technology development.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const ContactPage = () => (
    <div className={`section ${isDarkMode ? 'dark' : ''}`}>
      <div className="container">
        <h1 className="section-title gradient-text">Contact Us</h1>
        <div className="contact-grid">
          <Card className="contact-info">
            <h2 className="contact-heading">Get In Touch</h2>
            <div className="contact-item">
              <Mail color="#7c3aed" size={24} />
              <div>
                <h3>Email</h3>
                <p>info@pragpace.com</p>
              </div>
            </div>
            <div className="contact-item">
              <MapPin color="#3b82f6" size={24} />
              <div>
                <h3>Address</h3>
                <p>PRAGPACE SOLUTIONS PVT LTD<br />316 Vishwothama, JP Nagar 8th Phase, Jambusavari Dinne, Bengaluru, Karnataka, India, 560083</p>
              </div>
            </div>
          </Card>

          <Card className="map-card" style={{ height: "400px", borderRadius: "12px", overflow: "hidden" }}>
            <MapContainer
              center={[12.865914327547824, 77.57854897116414]}
              zoom={15}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[12.865914327547824, 77.57854897116414]}>
                <Popup>
                  <strong>PragPace Office</strong><br />
                  Bengaluru, India
                </Popup>
              </Marker>
            </MapContainer>
          </Card>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`app ${isDarkMode ? 'dark' : ''}`}>
      <NavBar />
      <div ref={homeRef}><HomePage /></div>
      <div ref={solutionsRef}><SolutionsPage /></div>
      <div ref={learnMoreRef}><LearnMorePage /></div>
      <div ref={aboutRef}><AboutPage /></div>
      <div ref={contactRef}><ContactPage /></div>

      <footer>
        <div className="footer-content">
          <div className="footer-section">
            <h2>PragPace</h2>
            <p>Empowering teams with intelligent project and portfolio management solutions for the modern enterprise.</p>
          </div>

          <div className="footer-section">
            <h3>Explore</h3>
            <ul>
              <li><button onClick={() => solutionsRef.current.scrollIntoView({ behavior: 'smooth' })}>Solutions</button></li>
              <li><button onClick={() => aboutRef.current.scrollIntoView({ behavior: 'smooth' })}>About</button></li>
              <li><button onClick={() => contactRef.current.scrollIntoView({ behavior: 'smooth' })}>Contact</button></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Useful Links</h3>
            <ul>
              <li><a href="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</a></li>
              <li><a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h3>Follow Us</h3>
            <div className="social-icons">
              <a href="https://www.facebook.com/PragPace/" target="_blank" rel="noopener noreferrer">
                <Facebook fontSize="large" />
              </a>
              <a href="https://www.instagram.com/pragpace/" target="_blank" rel="noopener noreferrer">
                <Instagram fontSize="large" />
              </a>
              <a href="https://www.youtube.com/@pragpace" target="_blank" rel="noopener noreferrer">
                <YouTube fontSize="large" />
              </a>
              <a href="https://www.linkedin.com/in/pragpace" target="_blank" rel="noopener noreferrer">
                <LinkedIn fontSize="large" />
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          © 2025 PragPace. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PragPaceWebsite;